import {
  COMPANY_RESOURCE_MANAGEMENT_API,
  MESSAGES,
  PEOPLE_RESOURCE_MANAGEMENT_API,
} from '@aibees/elephant/constants';
import { SearchMode, SearchType } from '@aibees/elephant/types';
import { HttpMethod } from '@aibees/shared/definition';
import { useNotify } from '@aibees/shared/hooks';
import { useBaseMutation } from '@aibees/shared/http';
import { useWorkspace } from '@aibees/shared/workspace';
import { HttpStatusCode } from 'axios';
import { ERROR_CODES } from '../constants/error-codes';
import { SaveRecordRequest } from './types';
import { useInvalidateCompanyAndPeopleQueries } from './use-invalidate-company-and-people-queries';
import { useSearchQueryFilter } from './use-search-query-filter';

export function useSaveRecord(type?: SearchType) {
  const notify = useNotify();
  const workspaceId = useWorkspace()?.id;
  const invalidateData = useInvalidateCompanyAndPeopleQueries(type, true);
  const { params } = useSearchQueryFilter({ select: ['value'] });

  const url =
    type === SearchType.People
      ? PEOPLE_RESOURCE_MANAGEMENT_API
      : COMPANY_RESOURCE_MANAGEMENT_API;

  const baseMutation = useBaseMutation<SaveRecordRequest, void>(
    {
      url: url.replace(':wid', String(workspaceId)).concat('?action=store'),
      method: HttpMethod.post,
      headers: { 'x-customer-id': workspaceId },
    },
    {
      onSuccess(_response, _request) {
        invalidateData();
      },
      onError(error, _request) {
        notify.closeSnackbar();
        if (error.status === HttpStatusCode.Conflict)
          notify.warning('Error', MESSAGES.ACTION_CONFLICT, undefined);
        else if (
          error.cause.code !== 402 &&
          error.cause.subCode !== ERROR_CODES.PROCESSING_EMAIL_ENRICHMENT
        )
          notify.error(error.message ?? 'An error occured.');
      },
      doNotShowErrorNotification: true,
    }
  );

  // Return wrapped mutation that adds type to request payload based on search params
  return {
    ...baseMutation,
    mutateAsync: (
      request: SaveRecordRequest,
      mutateOptions?: Parameters<typeof baseMutation.mutateAsync>[1]
    ) => {
      // Add type to request payload if similarity search is enabled
      const requestBody = params.value
        ? {
            ...request,
            type: SearchMode.Similarity,
          }
        : request;
      return baseMutation.mutateAsync(requestBody, mutateOptions);
    },
    mutate: (
      request: SaveRecordRequest,
      mutateOptions?: Parameters<typeof baseMutation.mutate>[1]
    ) => {
      // Add type to request payload if similarity search is enabled
      const requestBody = params.value
        ? {
            ...request,
            type: SearchMode.Similarity,
          }
        : request;
      return baseMutation.mutate(requestBody, mutateOptions);
    },
  };
}
